'use client';
import _debounce from 'lodash/debounce';
import { calculateBrowserWidth } from 'services/Ad/utils/browser';

// Configuration constants
const TIMING_CONFIG = {
  LEADERBOARD_OPEN_TIMER: 3000,
  LOAD_TIME_POLL: 250,
  SCROLL_DEBOUNCE: 200,
  SCROLL_THRESHOLD: 25,
} as const;

const BREAKPOINTS = {
  DESKTOP_AD_WIDTH: 820,
  MOBILE_AD_WIDTH: 576,
  TABLET_MIN: 768,
  DESKTOP_MIN: 1024,
} as const;

const DIRECT_SOLD_DIMENSIONS = {
  DESKTOP: { width: 970, height: 90 },
  MOBILE: { width: 300, height: 50 },
} as const;

const DEFAULT_HEIGHTS = {
  MOBILE: 110,
  TABLET: 160,
  DESKTOP: 300,
} as const;

// DOM selectors
const SELECTORS = {
  ROOT: ':root',
  LEADERBOARD: '#Leaderboard0',
  PARENT_LEADER: '.leaderboard-ad-wrapper-parent',
  LEADERBOARD_IFRAME: '.leaderboard-ad-wrapper-parent iframe',
  DIRECT_SOLD_IFRAME: '.cc-height iframe',
  STICKY_HUB: '.rr-sticky-hub',
} as const;

// CSS classes
const CSS_CLASSES = {
  CC_HEIGHT: 'cc-height',
  LEADERBOARD_STICKY: 'leaderboard--sticky',
  TEST: 'leaderboard-ad-wrapper-parent-test',
  LEADERBOARD_SCROLL: 'leaderboard--scroll',
  IS_STICKY: 'is-sticky',
  IS_STICKY_CC_HEIGHT: 'is-sticky-cc-height',
  LARGE: 'lg',
} as const;
type possibleClasses = typeof CSS_CLASSES.IS_STICKY | typeof CSS_CLASSES.IS_STICKY_CC_HEIGHT | typeof CSS_CLASSES.LARGE;
/**
 * Calculates the appropriate leaderboard height based on screen width
 */
function getLeaderboardHeight(clientWidth: number): number {
  if (clientWidth >= BREAKPOINTS.DESKTOP_MIN) {
    return DEFAULT_HEIGHTS.DESKTOP;
  }

  if (clientWidth >= BREAKPOINTS.TABLET_MIN) {
    return DEFAULT_HEIGHTS.TABLET;
  }

  return DEFAULT_HEIGHTS.MOBILE;
}

/**
 * Checks if an ad is a direct sold ad based on its dimensions
 */
function isDirectSoldAd(width: number | undefined, height: number | undefined): boolean {
  if (!width || !height) return false;

  const isDesktopDirectSold =
    width === DIRECT_SOLD_DIMENSIONS.DESKTOP.width && height === DIRECT_SOLD_DIMENSIONS.DESKTOP.height;

  const isMobileDirectSold =
    width === DIRECT_SOLD_DIMENSIONS.MOBILE.width && height === DIRECT_SOLD_DIMENSIONS.MOBILE.height;

  return isDesktopDirectSold || isMobileDirectSold;
}

/**
 * Sets CSS custom property for leaderboard height
 */
export function setLeaderboardHeight(height: number): void {
  const root = document.querySelector<HTMLElement>(SELECTORS.ROOT);
  root?.style.setProperty('--ld-height', `${height}px`);
}

/**
 * Handles scroll behavior for direct sold ads
 */
function handleDirectSoldScroll(leaderboardHeight: number): void {
  const directSoldIframe = document.querySelector(SELECTORS.DIRECT_SOLD_IFRAME) as HTMLElement;

  if (directSoldIframe?.clientHeight) {
    const currentHeight = directSoldIframe.clientHeight;
    const heightToSet = window.scrollY === 0 ? leaderboardHeight : currentHeight;
    setLeaderboardHeight(heightToSet);
  } else {
    setLeaderboardHeight(0);
  }
}

/**
 * Sets up scroll listener for direct sold ads
 */
function setupDirectSoldScrollListener(leaderboardHeight: number): void {
  const debouncedScrollHandler = _debounce(
    () => handleDirectSoldScroll(leaderboardHeight),
    TIMING_CONFIG.SCROLL_DEBOUNCE,
  );

  document.addEventListener('scroll', debouncedScrollHandler);
}

/**
 * Handles direct sold ad behavior
 */
function handleDirectSoldAd(leaderboardHeight: number, parentLeader: Element): void {
  setTimeout(() => {
    if (parentLeader.classList.contains(CSS_CLASSES.CC_HEIGHT)) {
      requestAnimationFrame(() => {
        parentLeader.classList.remove(CSS_CLASSES.LEADERBOARD_SCROLL);
        setLeaderboardHeight(leaderboardHeight);
        setupDirectSoldScrollListener(leaderboardHeight);
      });
    }
  }, TIMING_CONFIG.LEADERBOARD_OPEN_TIMER);
}

/**
 * Creates scroll listener for non-direct sold ads
 */
function createNonDirectSoldScrollListener(parentLeader: Element): () => void {
  return () => {
    if (window.scrollY > TIMING_CONFIG.SCROLL_THRESHOLD) {
      parentLeader.classList.add(CSS_CLASSES.LEADERBOARD_SCROLL);
      document.removeEventListener('scroll', createNonDirectSoldScrollListener(parentLeader));
    }
  };
}

/**
 * Applies sticky hub classes based on ad type and conditions
 */
function applyStickyHubClasses(
  isDirectSold: boolean,
  parentLeader: Element,
  clientWidth: number,
  stickyFlag: boolean,
): void {
  const stickyHub = document.querySelector(SELECTORS.STICKY_HUB);
  if (!stickyHub) return;

  const hasConsentHeight = parentLeader.classList.contains(CSS_CLASSES.CC_HEIGHT);

  if (isDirectSold || hasConsentHeight) {
    stickyHub.classList.add(CSS_CLASSES.IS_STICKY_CC_HEIGHT, CSS_CLASSES.LARGE);
  } else {
    const classesToAdd: possibleClasses[] = [CSS_CLASSES.IS_STICKY];
    if (!stickyFlag || clientWidth < BREAKPOINTS.MOBILE_AD_WIDTH) {
      classesToAdd.push(CSS_CLASSES.LARGE);
    }
    stickyHub.classList.add(...classesToAdd);
  }
}

/**
 * Applies initial sticky behavior to the leaderboard
 */
export function applyInitialStickyBehavior(): void {
  const parentLeader = document.querySelector(SELECTORS.PARENT_LEADER);
  // Set initial leaderboard height
  const clientWidth = calculateBrowserWidth();
  const leaderboardHeight = getLeaderboardHeight(clientWidth);
  setLeaderboardHeight(leaderboardHeight);

  // Add sticky class to parent leaderboard element
  parentLeader.classList.add(CSS_CLASSES.LEADERBOARD_STICKY);
  parentLeader.classList.add(CSS_CLASSES.TEST);
}

/**
 * Handles non-direct sold ad behavior
 */
function handleNonDirectSoldAd(
  leaderboardHeight: number,
  adHeight: number | undefined,
  clientWidth: number,
  parentLeader: Element,
): void {
  let stickyFlag = true;

  // Initial setup
  if (stickyFlag) {
    setLeaderboardHeight(leaderboardHeight);
    parentLeader.classList.add(CSS_CLASSES.LEADERBOARD_STICKY);

    if (clientWidth <= BREAKPOINTS.DESKTOP_AD_WIDTH && adHeight) {
      requestAnimationFrame(() => {
        setLeaderboardHeight(adHeight);
        const scrollListener = createNonDirectSoldScrollListener(parentLeader);
        document.addEventListener('scroll', scrollListener);
      });
    }
  }

  // Remove sticky behavior after timer
  setTimeout(() => {
    stickyFlag = false;
    if (clientWidth > BREAKPOINTS.DESKTOP_AD_WIDTH) {
      parentLeader.classList.remove(CSS_CLASSES.LEADERBOARD_STICKY);
      setLeaderboardHeight(0);
    }
  }, TIMING_CONFIG.LEADERBOARD_OPEN_TIMER);

  // Apply sticky hub classes
  if (stickyFlag && clientWidth >= BREAKPOINTS.MOBILE_AD_WIDTH) {
    setTimeout(() => {
      applyStickyHubClasses(false, parentLeader, clientWidth, stickyFlag);
    }, TIMING_CONFIG.LEADERBOARD_OPEN_TIMER);
  }
}

/**
 * Main leaderboard manager function
 */
export const leaderboardManager = (): void => {
  const leaderboardCheck = setInterval(() => {
    // Get DOM elements and measurements
    const clientWidth = calculateBrowserWidth();
    const parentLeader = document.querySelector(SELECTORS.PARENT_LEADER);
    const leaderboardIframe = document.querySelector(SELECTORS.LEADERBOARD_IFRAME) as HTMLIFrameElement;
    const leaderboardHeight = getLeaderboardHeight(clientWidth);

    if (!parentLeader || !leaderboardIframe) {
      return;
    }

    // Check if the ad inside the Google iframe has loaded
    let adLoaded = false;
    // Attempt to access the iframe's document
    const iframeDoc = leaderboardIframe.contentDocument;
    if (iframeDoc) {
      adLoaded = leaderboardIframe.clientHeight > 0 && leaderboardIframe.clientWidth > 0;
    }

    if (!adLoaded) {
      return;
    }

    const adWidth = leaderboardIframe.clientWidth;
    const adHeight = leaderboardIframe.clientHeight;
    if (!adWidth || !adHeight) {
      return;
    }
    const isDirectSold = isDirectSoldAd(adWidth, adHeight);
    // Leaderboard has loaded, stop polling
    clearInterval(leaderboardCheck);
    // Handle different ad types
    if (isDirectSold) {
      handleDirectSoldAd(leaderboardHeight, parentLeader);
      applyStickyHubClasses(true, parentLeader, clientWidth, true);
    } else {
      handleNonDirectSoldAd(leaderboardHeight, adHeight, clientWidth, parentLeader);
    }
  }, TIMING_CONFIG.LOAD_TIME_POLL);
};
